import React, { useState, useRef, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiMail } = FiIcons;

const InputArea = ({ onSend, disabled, requireEmail, onSubmitEmail }) => {
  const [input, setInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const textareaRef = useRef(null);

  const isChatDisabled = disabled || requireEmail;

  // Stricter Email Validation: Requires standard formatting and max length of 100 characters.
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length > 0 && email.length <= 100;
  };

  const handleSend = () => {
    if (input.trim() && !isChatDisabled) {
      onSend(input);
      setInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmailSubmit = () => {
    if (isValidEmail(emailInput)) {
      onSubmitEmail(emailInput.trim());
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  return (
    <div className="px-4 py-4 border-t border-gray-100 bg-white flex flex-col gap-3">
      {/* Highlighted Email Request Box */}
      {requireEmail && (
        <div 
          className="max-w-3xl mx-auto w-full p-4 rounded-xl shadow-sm relative overflow-hidden transition-all duration-500 ease-in-out"
          style={{ backgroundColor: '#fdf8f3', border: '1px solid #d4b78f' }}
        >
          <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: '#a07840' }} />
          <p className="text-[14.5px] font-bold mb-3 tracking-tight" style={{ color: '#543e18' }}>
            <SafeIcon icon={FiMail} className="inline mr-2 mb-0.5 w-4 h-4" />
            Please provide your work email so that one of our community experts can answer you precisely and you can get answers directly in your inbox.
          </p>
          <div className="flex gap-2.5">
            <input
              type="email"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder="your.name@company.com"
              maxLength={100}
              className="flex-1 px-3.5 py-2 text-[14px] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a07840] bg-white transition-all shadow-inner"
              style={{ border: '1px solid #e8d9c4', color: '#3d2c0f' }}
              onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()}
            />
            <button
              onClick={handleEmailSubmit}
              disabled={!isValidEmail(emailInput)}
              className="px-5 py-2 text-white text-[14px] font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md active:scale-95"
              style={{ background: 'linear-gradient(135deg, #6b4f28, #3d2c0f)' }}
            >
              Submit Email
            </button>
          </div>
        </div>
      )}

      {/* Main Chat Input */}
      <div 
        className="max-w-3xl mx-auto flex items-end gap-3 w-full transition-opacity duration-300"
        style={{ opacity: requireEmail ? 0.4 : 1 }}
      >
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={requireEmail ? "Please submit your email above to continue..." : "Continue the conversation…"}
            disabled={isChatDisabled}
            rows={1}
            className="w-full bg-transparent border-0 border-b-2 outline-none text-[14px] text-gray-800 placeholder-gray-400 resize-none pb-2 pt-1 transition-colors duration-300 disabled:cursor-not-allowed"
            style={{ 
              borderBottomColor: input.trim() && !isChatDisabled ? '#543e18' : '#e5e7eb',
              maxHeight: '120px'
            }}
          />
          <div 
            className="absolute bottom-0 left-0 h-0.5 rounded-full transition-all duration-500"
            style={{ 
              width: input.trim() && !isChatDisabled ? '100%' : '0%',
              background: 'linear-gradient(90deg, #543e18, #a07840)',
            }}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!input.trim() || isChatDisabled}
          className="flex-shrink-0 mb-1 transition-all duration-200 hover:scale-110 active:scale-95 disabled:hover:scale-100 disabled:active:scale-100"
          style={{ opacity: input.trim() && !isChatDisabled ? 1 : 0.25 }}
        >
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: input.trim() && !isChatDisabled ? 'linear-gradient(135deg, #6b4f28, #3d2c0f)' : '#e5e7eb' }}
          >
            <SafeIcon icon={FiSend} className="w-3.5 h-3.5 text-white" />
          </div>
        </button>
      </div>
    </div>
  );
};

export default InputArea;